import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import searchIcon from '../../assets/icons/search.svg';

type Academy = {
  id: string;
  name: string;
  city?: string;
  state?: string;
  country?: string;
  is_custom?: boolean;
};

interface AcademyAutocompleteProps {
  value: string | { id: string; name: string } | '';
  onChange: (value: string | { id: string; name: string }) => void;
  placeholder?: string;
  className?: string;
  containerClassName?: string;
  inputGap?: string;
}

export function AcademyAutocomplete({ value, onChange, placeholder, className, containerClassName }: AcademyAutocompleteProps) {
  const [filteredAcademies, setFilteredAcademies] = useState<Academy[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Extract display value from the value prop
  const displayValue = typeof value === 'string' ? value : (value && typeof value === 'object' ? value.name : '');

  useEffect(() => {
    setInputValue(displayValue);
  }, [displayValue]);

  useEffect(() => {
    const fetchAcademies = async () => {
      if (!inputValue || inputValue.length < 1) {
        // Show all academies when input is empty or has less than 1 character
        setLoading(true);
        try {
          const { data, error } = await supabase
            .from('academies')
            .select('*')
            .order('name')
            .limit(20); // Limit to first 20 academies for performance
          if (error) {
            console.error('Error fetching academies:', error);
            setFilteredAcademies([]);
          } else {
            setFilteredAcademies(data || []);
          }
        } catch (error) {
          console.error('Error fetching academies:', error);
          setFilteredAcademies([]);
        } finally {
          setLoading(false);
        }
        return;
      }
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('academies')
          .select('*')
          .ilike('name', `%${inputValue}%`)
          .order('name');
        if (error) {
          console.error('Error fetching academies:', error);
          setFilteredAcademies([]);
        } else {
          setFilteredAcademies(data || []);
        }
      } catch (error) {
        console.error('Error fetching academies:', error);
        setFilteredAcademies([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAcademies();
  }, [inputValue]);

  const handleSelect = (academy: Academy) => {
    onChange({ id: academy.id, name: academy.name });
    setOpen(false);
  };

  const handleCustomAcademy = async (academyName: string) => {
    try {
      
      // Create custom academy in the database
      const { data, error } = await supabase.rpc('create_or_get_custom_academy', {
        academy_name: academyName
      });
      
      
      
      if (error) {
        console.error('Error creating custom academy:', error);
        onChange({ id: null, name: academyName });
      } else {
        // Academy was created/found, use the name

        onChange({ id: data, name: academyName });
      }
    } catch (error) {
      console.error('Error creating custom academy:', error);
      onChange({ id: null, name: academyName });
    }
    setOpen(false);
  };

  const handleInputChange = (inputValue: string) => {
    setInputValue(inputValue);
    setOpen(true);
  };

  return (
    <div className={cn("relative", containerClassName)}>
      <div className="relative">
        <img
          src={searchIcon}
          alt="Search"
          className={cn(
            "absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5",
            containerClassName?.includes('bg-white') || className?.includes('bg-white')
              ? "brightness-0 opacity-40"
              : "brightness-0 invert opacity-60"
          )}
        />
        <input
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className={cn(
            "w-full border h-12 rounded-md pl-12 pr-12 text-[12px] focus:outline-none transition-all duration-200 font-sans",
            // Check if we're in a white background context (like a modal)
            containerClassName?.includes('bg-white') || className?.includes('bg-white')
              ? "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500"
              : "border-white text-white placeholder-white/60 bg-white/5",
            className
          )}
          style={{ 
            zIndex: 1,
            borderWidth: '1px',
            borderStyle: 'solid'
          }}
        />
        {inputValue && (
          <button
            onClick={() => {
              setInputValue('');
              onChange('');
            }}
            className={cn(
              "absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200",
              containerClassName?.includes('bg-white') || className?.includes('bg-white')
                ? "text-gray-400 hover:text-gray-600"
                : "text-white/60 hover:text-white"
            )}
            aria-label="Clear search"
            style={{ zIndex: 2 }}
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-black rounded-md shadow-lg max-h-60 overflow-auto">
          {loading ? (
            <div className="p-4 text-center text-sm text-white/60">
              Loading academies...
            </div>
          ) : filteredAcademies.length === 0 && inputValue.length >= 2 ? (
            <div className="py-1">
              <div
                className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-lg text-white hover:bg-white/10 focus:bg-white/10"
                onClick={() => handleCustomAcademy(inputValue)}
              >
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                  <Check className="h-4 w-4 opacity-0" />
                </span>
                <span className="text-white">Add "{inputValue}"</span>
              </div>
            </div>
          ) : (
            <div className="py-1">
              {filteredAcademies.map((academy) => (
                <div
                  key={academy.id}
                  className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-lg text-white hover:bg-white/10 focus:bg-white/10"
                  onClick={() => handleSelect(academy)}
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    {inputValue === academy.name && <Check className="h-4 w-4 text-white" />}
                  </span>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-white">{academy.name}</span>
                    <span className="text-xs text-white/60">
                      {academy.city && academy.state ? `${academy.city}, ${academy.state}` : academy.city || academy.state || ''}
                      {academy.country && (academy.city || academy.state) ? `, ${academy.country}` : academy.country || ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
} 